import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DataService } from '../../services/data.service';
import { AuthenticationService } from '../../services/authentication.service';
import { Message } from '../../models/message.model';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-messages-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatChipsModule,
    MatDividerModule
  ],
  template: `
    <h2 mat-dialog-title>
      <div class="title-row">
        <span>Messages</span>
        @if (unreadCount > 0) {
          <span class="message-count">{{ unreadCount }} unread</span>
        }
      </div>
    </h2>
    <mat-dialog-content>
      <div class="messages-container">
        @if (messages.length === 0) {
          <div class="no-messages">
            <mat-icon>inbox</mat-icon>
            <p>No messages</p>
          </div>
        }
    
        @for (message of messages; track message) {
          <div class="message-card" [class.unread]="!message.isViewed">
            <div class="message-header">
              <div class="header-left">
                @if (!message.isViewed) {
                  <mat-icon class="unread-indicator">fiber_manual_record</mat-icon>
                }
                <div class="message-meta">
                  <span class="sender">{{ message.fromUserId ? 'User #' + message.fromUserId : 'System' }}</span>
                  <span class="date">{{ message.createdDateTime | date:'short' }}</span>
                </div>
              </div>
              <div class="header-right">
                @if (message.isGlobal) {
                  <mat-chip class="audience-chip">
                    <mat-icon>public</mat-icon>
                    Everyone
                  </mat-chip>
                }
                <button mat-icon-button color="warn" (click)="deleteMessage(message)" matTooltip="Delete message">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>
            <div class="message-content" (click)="markAsRead(message)">
              <h3 class="subject">{{ message.subject || '(No Subject)' }}</h3>
              <p class="body">{{ message.body || '(No content)' }}</p>
            </div>
          </div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      @if (unreadCount > 0) {
        <button mat-button (click)="markAllAsRead()">
          Mark All as Read
        </button>
      }
      <button mat-raised-button color="primary" (click)="onClose()">
        Close
      </button>
    </mat-dialog-actions>
    `,
  styleUrl: './messages-dialog.component.scss'
})
export class MessagesDialogComponent implements OnInit {
  messages: Message[] = [];
  unreadCount: number = 0;
  currentUserId: number = 0;

  constructor(
    public dialogRef: MatDialogRef<MessagesDialogComponent>,
    private dialog: MatDialog,
    private dataService: DataService,
    private authService: AuthenticationService
  ) { }

  async ngOnInit(): Promise<void> {
    // Get current user ID from auth service
    const currentUser = this.authService.currentUserValue;
    if (currentUser?.user?.id) {
      this.currentUserId = currentUser.user.id;
      await this.loadMessages();
    }
  }

  async loadMessages(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      // Fetch messages for current user
      this.messages = await firstValueFrom(
        this.dataService.getMessages(this.currentUserId)
      );
      this.updateUnreadCount();
    } catch (error) {
      console.error('Error loading messages:', error);
      this.messages = [];
      this.updateUnreadCount();
    }
  }

  updateUnreadCount(): void {
    this.unreadCount = this.messages.filter(m => !m.isViewed).length;
  }

  async markAsRead(message: Message): Promise<void> {
    if (!message.isViewed && message.id) {
      try {
        await firstValueFrom(this.dataService.markMessageAsRead(message.id));
        message.isViewed = true;
        message.viewedDateTime = new Date();
        this.updateUnreadCount();
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  }

  async markAllAsRead(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      await firstValueFrom(this.dataService.markAllMessagesAsRead(this.currentUserId));
      this.messages.forEach(m => {
        m.isViewed = true;
        m.viewedDateTime = new Date();
      });
      this.updateUnreadCount();
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  }

  deleteMessage(message: Message): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Message',
        message: `Are you sure you want to delete the message "${message.subject}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmColor: 'warn',
        icon: 'delete'
      }
    });

    dialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed && message.id) {
        try {
          await firstValueFrom(this.dataService.deleteMessage(message.id));
          this.messages = this.messages.filter(m => m.id !== message.id);
          this.updateUnreadCount();
        } catch (error) {
          console.error('Error deleting message:', error);
        }
      }
    });
  }

  onClose(): void {
    this.dialogRef.close(this.unreadCount);
  }
}
